import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

/**
 * ChatMarkdownRenderer - Renders markdown content in chat messages
 * 
 * Optimized for chat widget with compact styling and proper formatting
 * for AI responses that may contain markdown elements.
 * 
 * @author Opptymizer
 * @version 1.0.0
 */

interface ChatMarkdownRendererProps {
  content: string;
  isUser: boolean;
}

const ChatMarkdownRenderer: React.FC<ChatMarkdownRendererProps> = ({ content, isUser }) => {
  return (
    <div className={`
      prose prose-sm max-w-none break-words overflow-wrap-anywhere
      ${isUser ? 'prose-invert' : ''}
      prose-headings:font-medium prose-headings:mb-2 prose-headings:mt-3
      prose-h1:text-sm prose-h1:mb-2 prose-h1:mt-3
      prose-h2:text-sm prose-h2:mb-2 prose-h2:mt-3  
      prose-h3:text-xs prose-h3:mb-1 prose-h3:mt-2
      prose-p:mb-3 prose-p:leading-snug prose-p:text-sm prose-p:break-words
      prose-a:text-blue-400 prose-a:font-medium prose-a:no-underline hover:prose-a:underline prose-a:break-all
      prose-strong:font-medium 
      prose-ul:my-2 prose-ul:space-y-1 prose-li:my-0 prose-li:text-sm prose-li:break-words
      prose-ol:my-2 prose-ol:space-y-1
      prose-blockquote:text-sm prose-blockquote:border-l-2 prose-blockquote:border-blue-300 prose-blockquote:bg-blue-50 prose-blockquote:py-1 prose-blockquote:px-2 prose-blockquote:rounded-r prose-blockquote:my-2 prose-blockquote:break-words
      prose-code:text-xs prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:break-all
      prose-pre:text-xs prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-2 prose-pre:rounded prose-pre:my-2 prose-pre:overflow-x-auto
      prose-table:text-xs prose-table:border-collapse prose-table:w-full prose-table:my-2
      prose-thead:bg-gray-50 prose-th:p-1 prose-th:text-left prose-th:font-medium prose-th:border prose-th:border-gray-200
      prose-td:p-1 prose-td:border prose-td:border-gray-200
      prose-img:rounded prose-img:my-2 prose-img:max-w-full prose-img:h-auto
    `}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Custom rendering for images - compact for chat
          img: ({ node, ...props }) => (
            <img 
              {...props} 
              className="rounded my-2 max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
              loading="lazy"
              onClick={() => {
                // Open image in new tab when clicked
                if (props.src) {
                  window.open(props.src, '_blank');
                }
              }}
            />
          ),
          
          // Custom rendering for tables - compact and scrollable
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2 border rounded">
              <table {...props} className="min-w-full text-xs" />
            </div>
          ),
          
          // Custom rendering for table headers
          th: ({ node, ...props }) => (
            <th {...props} className="px-2 py-1 text-left text-xs font-medium bg-gray-50 border-b" />
          ),
          
          // Custom rendering for table cells
          td: ({ node, ...props }) => (
            <td {...props} className="px-2 py-1 text-xs border-b" />
          ),
          
          // Custom rendering for links - open in new tab with proper text wrapping
          a: ({ node, ...props }) => (
            <a 
              {...props} 
              className={`font-medium no-underline hover:underline transition-colors break-all ${
                isUser ? 'text-blue-200 hover:text-blue-100' : 'text-blue-600 hover:text-blue-800'
              }`}
              target="_blank" 
              rel="noopener noreferrer" 
            />
          ),
          
          // Custom rendering for lists - compact spacing with nested list handling
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-disc pl-4 space-y-0.5 my-2 text-sm [&_ul]:my-1 [&_ol]:my-1" />
          ),
          
          ol: ({ node, ...props }) => (
            <ol {...props} className="list-decimal pl-4 space-y-0.5 my-2 text-sm [&_ul]:my-1 [&_ol]:my-1" />
          ),
          
          // Custom rendering for list items with proper text wrapping and tight line-height
          li: ({ node, ...props }) => (
            <li {...props} className="text-sm leading-snug break-words" />
          ),
          
          // Custom rendering for code blocks
          code: ({ node, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match;
            
            if (isInline) {
              return (
                <code 
                  className={`text-xs font-mono px-1 py-0.5 rounded ${
                    isUser 
                      ? 'bg-blue-800 text-blue-100' 
                      : 'bg-gray-100 text-gray-800'
                  }`} 
                  {...props}
                >
                  {children}
                </code>
              );
            }
            
            return (
              <div className="my-2">
                <pre className="text-xs bg-gray-900 text-gray-100 p-2 rounded overflow-x-auto">
                  <code className={className} {...props}>
                    {children}
                  </code>
                </pre>
              </div>
            );
          },
          
          // Custom rendering for headings - compact for chat
          h1: ({ node, ...props }) => (
            <h1 {...props} className="text-sm font-semibold mt-3 mb-2 first:mt-0" />
          ),
          
          h2: ({ node, ...props }) => (
            <h2 {...props} className="text-sm font-semibold mt-3 mb-2 first:mt-0" />
          ),
          
          h3: ({ node, ...props }) => (
            <h3 {...props} className="text-xs font-semibold mt-2 mb-1 first:mt-0" />
          ),
          
          h4: ({ node, ...props }) => (
            <h4 {...props} className="text-xs font-medium mt-2 mb-1 first:mt-0" />
          ),
          
          // Custom rendering for paragraphs - margin between paragraphs, tight line-height within, proper text wrapping
          p: ({ node, ...props }) => (
            <p {...props} className="text-sm leading-snug mb-3 last:mb-0 first:mt-0 break-words" />
          ),
          
          // Custom rendering for blockquotes
          blockquote: ({ node, ...props }) => (
            <blockquote 
              {...props} 
              className={`text-sm border-l-2 py-1 px-2 rounded-r my-2 italic ${
                isUser 
                  ? 'border-blue-300 bg-blue-800 bg-opacity-30' 
                  : 'border-blue-300 bg-blue-50'
              }`}
            />
          ),
          
          // Custom rendering for horizontal rules
          hr: ({ node, ...props }) => (
            <hr {...props} className="my-3 border-gray-300" />
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default ChatMarkdownRenderer;
